import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { fetchAuthenticatedImage, closeSession, deleteImage } from '../services/api';
import { getWebSocketUrl, API_BASE_URL } from '../config';
import authService from '../services/authService';
import JSZip from 'jszip';

function AppraiserConsole() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const roomId = searchParams.get('room');
  
  const [status, setStatus] = useState('');
  const [joined, setJoined] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [audioMuted, setAudioMuted] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [photoBlobUrls, setPhotoBlobUrls] = useState({});
  const [log, setLog] = useState('');
  const [homeownerLocation, setHomeownerLocation] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('inactive'); // 'active' or 'inactive'
  const [peerPresent, setPeerPresent] = useState(false);
  const peerPresentRef = useRef(false);
  
  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const videoContainerRef = useRef(null);
  
  const remoteVideoRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const pendingCandidatesRef = useRef([]);
  const seenPhotosRef = useRef(new Set());
  const blobUrlsToCleanupRef = useRef([]);

  const addLog = (message) => {
    setLog(prev => prev + message + '\n');
  };

  const addPhoto = async (url) => {
    if (!url || seenPhotosRef.current.has(url)) return;
    seenPhotosRef.current.add(url);
    setPhotos(prev => [url, ...prev]);
    
    // Fetch the authenticated image and create a blob URL
    try {
      const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
      const blobUrl = await fetchAuthenticatedImage(fullUrl);
      blobUrlsToCleanupRef.current.push(blobUrl); // Track for cleanup
      setPhotoBlobUrls(prev => ({ ...prev, [url]: blobUrl }));
    } catch (error) {
      addLog('Error loading image: ' + error.message);
    }
  };

  // Helper function to safely add tracks to peer connection
  const addLocalStreamToPeerConnection = (pc, stream) => {
    if (!pc || !stream) return;
    
    try {
      // Check which tracks are already added
      const existingTracks = new Set();
      pc.getSenders().forEach(sender => {
        if (sender.track) {
          existingTracks.add(sender.track.id);
        }
      });
      
      // Only add tracks that aren't already added
      stream.getTracks().forEach(track => {
        if (!existingTracks.has(track.id)) {
          pc.addTrack(track, stream);
        }
      });
    } catch (e) {
      addLog('Error adding tracks to peer connection: ' + e.message);
    }
  };

  const makePeerConnection = () => {
    // Close existing connection if any
    if (pcRef.current) {
      try {
        pcRef.current.close();
      } catch (e) {
        addLog('Error closing old peer connection: ' + e.message);
      }
    }
    
    // Clear any pending ICE candidates
    pendingCandidatesRef.current = [];
    
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
    });

    pc.onicecandidate = (e) => {
      if (e.candidate) {
        wsSend({ type: 'signal', roomId, data: { ice: e.candidate } });
      }
    };

    pc.ontrack = (ev) => {
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = ev.streams[0];
        remoteVideoRef.current.muted = false; // Auto-enable audio
        remoteVideoRef.current.play().catch(() => {});
        setAudioMuted(false); // Update state to reflect audio is enabled
        setStatus('Connected. You can capture photos.');
        setConnectionStatus('active');
      }
    };

    pc.onconnectionstatechange = () => {
      const state = pc.connectionState;
      // Update connection status: active only if peer is present AND connection is established
      // Use a ref to get the current peerPresent value since closure might have stale value
      if (peerPresentRef.current && state === 'connected') {
        setConnectionStatus('active');
      } else if (!peerPresentRef.current || state === 'disconnected' || state === 'failed' || state === 'closed') {
        setConnectionStatus('inactive');
      }
      
      if (state === 'failed') {
        // Try to restart ICE
        pc.restartIce().catch(() => {});
      }
    };
    
    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      // Update connection status: active only if peer is present AND ICE is connected
      if (peerPresentRef.current && state === 'connected') {
        setConnectionStatus('active');
      } else if (state === 'failed' || state === 'disconnected') {
        setConnectionStatus('inactive');
        // Try to restart ICE
        pc.restartIce().catch(() => {});
      }
    };

    return pc;
  };

  const startLocal = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      localStreamRef.current = stream;
      
      if (pcRef.current) {
        addLocalStreamToPeerConnection(pcRef.current, stream);
      }
      addLog('Local audio added.');
    } catch (error) {
      addLog('Error getting local media: ' + error.message);
    }
  };

  const wsSend = (obj) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(obj));
    }
  };

  const beginOfferFlow = async () => {
    // Always create fresh peer connection when starting offer flow
    // This ensures we don't have stale connections from previous attempts
    pcRef.current = makePeerConnection();
    
    if (!localStreamRef.current) {
      await startLocal();
    }
    
    // Ensure local stream is added
    if (localStreamRef.current) {
      addLocalStreamToPeerConnection(pcRef.current, localStreamRef.current);
    }

    try {
      const offer = await pcRef.current.createOffer({ 
        offerToReceiveAudio: true, 
        offerToReceiveVideo: true 
      });
      await pcRef.current.setLocalDescription(offer);
      wsSend({ type: 'signal', roomId, data: { sdp: pcRef.current.localDescription } });
      addLog('Created and sent offer.');
    } catch (e) {
      addLog('Error creating offer: ' + e.message);
      // Retry once with fresh connection
      try {
        pcRef.current = makePeerConnection();
        if (localStreamRef.current) {
          addLocalStreamToPeerConnection(pcRef.current, localStreamRef.current);
        }
        const offer = await pcRef.current.createOffer({ 
          offerToReceiveAudio: true, 
          offerToReceiveVideo: true 
        });
        await pcRef.current.setLocalDescription(offer);
        wsSend({ type: 'signal', roomId, data: { sdp: pcRef.current.localDescription } });
        addLog('Retried and sent offer.');
      } catch (retryError) {
        addLog('Retry failed: ' + retryError.message);
        setStatus('Failed to create connection.');
      }
    }
  };

  const handleJoin = async () => {
    if (!roomId) {
      alert('Missing room id.');
      return;
    }

    // Clear any previous error
    setJoinError('');
    addLog('Joining...');
    setStatus('Connecting...');

    const wsBaseUrl = getWebSocketUrl();
    const ws = new WebSocket(`${wsBaseUrl}/ws`);
    wsRef.current = ws;

    ws.onopen = () => {
      const token = authService.getToken();
      ws.send(JSON.stringify({ type: 'join', roomId, role: 'appraiser', token }));
    };

    ws.onmessage = async (ev) => {
      const msg = JSON.parse(ev.data);
      
      // Debug log all messages
      console.log('AppraiserConsole received:', msg);

      if (msg.type === 'joined') {
        addLog('Joined as ' + msg.role);
        setJoined(true);
        setJoinError(''); // Clear any previous error
      }

      if (msg.type === 'peer-ready') {
        setPeerPresent(true);
        peerPresentRef.current = true;
        await beginOfferFlow();
        setStatus('Connecting...');
      }

      if (msg.type === 'signal') {
        if (msg.data?.sdp) {
          const desc = msg.data.sdp;
          if (!pcRef.current) {
            pcRef.current = makePeerConnection();
          }
          if (!localStreamRef.current) {
            await startLocal();
          }
          
          // Ensure local stream is added to peer connection (safely)
          if (localStreamRef.current) {
            addLocalStreamToPeerConnection(pcRef.current, localStreamRef.current);
          }

          try {
            await pcRef.current.setRemoteDescription(desc);

            for (const c of pendingCandidatesRef.current) {
              try {
                await pcRef.current.addIceCandidate(c);
              } catch (e) {
                addLog('queued ICE error: ' + e.message);
              }
            }
            pendingCandidatesRef.current = [];

            if (desc.type === 'answer') {
              setStatus('Connected.');
              // Status will update via onconnectionstatechange when connection is established
            }
          } catch (e) {
            addLog('Error setting remote description: ' + e.message);
            // If setting remote description fails, recreate connection
            if (e.name === 'InvalidStateError' || e.message.includes('state')) {
              addLog('Recreating peer connection due to invalid state');
              pcRef.current = makePeerConnection();
              if (localStreamRef.current) {
                addLocalStreamToPeerConnection(pcRef.current, localStreamRef.current);
              }
              // Retry setting remote description
              try {
                await pcRef.current.setRemoteDescription(desc);
                if (desc.type === 'answer') {
                  addLog('Handshake complete (retry).');
                  setStatus('Connected.');
                }
              } catch (retryError) {
                addLog('Retry failed: ' + retryError.message);
              }
            }
          }
        }

        if (msg.data?.ice) {
          if (pcRef.current && pcRef.current.remoteDescription) {
            try {
              await pcRef.current.addIceCandidate(msg.data.ice);
            } catch (e) {
              addLog('addIceCandidate error: ' + e.message);
            }
          } else {
            pendingCandidatesRef.current.push(msg.data.ice);
          }
        }
      }

      if (msg.type === 'photo_uploaded') {
        setStatus('Photo uploaded.');
        addPhoto(msg.url);
        addLog('Photo uploaded: ' + msg.url);
      }

      if (msg.type === 'location_update') {
        console.log('Location update received:', msg);
        const { latitude, longitude, accuracy } = msg;
        if (latitude && longitude) {
          setHomeownerLocation({ latitude, longitude, accuracy: accuracy || 0 });
          addLog(`📍 Homeowner location received: ${latitude.toFixed(6)}, ${longitude.toFixed(6)} (±${Math.round(accuracy || 0)}m)`);
        } else {
          addLog('Invalid location data received from homeowner');
        }
      }

      if (msg.type === 'peer-left') {
        setPeerPresent(false);
        peerPresentRef.current = false;
        setConnectionStatus('inactive');
        setStatus('Peer left.');
        setHomeownerLocation(null);
      }

      if (msg.type === 'error') {
        const errorMsg = msg.message || msg.reason || 'Unknown error';
        addLog('Error: ' + errorMsg);
        
        // Reset state on error
        setJoined(false);
        setStatus('');
        
        // Display error message under the button
        setJoinError('Unable to join session. ' + errorMsg);
      }

      // Handle ping/pong keepalive to prevent ALB timeout
      if (msg.type === 'ping') {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'pong' }));
        }
      }
    };

    ws.onclose = (event) => {
      setConnectionStatus('inactive');
      setPeerPresent(false);
      peerPresentRef.current = false;
      
      // If closed before joining successfully (not a normal closure), show error
      if (!joined && event.code !== 1000) {
        addLog('Connection closed before joining: ' + (event.reason || 'Unknown reason'));
        setJoinError('Unable to join session. Connection closed unexpectedly.');
      }
    };
    
    ws.onerror = (error) => {
      addLog('WebSocket error occurred');
      if (!joined) {
        setJoinError('Unable to join session. Connection error occurred.');
      }
    };
  };

  const handleToggleAudio = () => {
    if (remoteVideoRef.current) {
      const newMutedState = !audioMuted;
      remoteVideoRef.current.muted = newMutedState;
      setAudioMuted(newMutedState);
      addLog(newMutedState ? 'Audio muted.' : 'Audio unmuted.');
    }
  };

  const handleCapturePhoto = async () => {
    try {
      if (!roomId) {
        alert('Missing room id.');
        return;
      }

      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        setStatus('WebSocket not connected.');
        addLog('Cannot capture: WebSocket not connected.');
        return;
      }

      // Send capture request to homeowner to trigger their camera
      wsSend({ type: 'capture_request', roomId });
      setStatus('Photo capture requested...');
      addLog('Sent capture request to homeowner.');
    } catch (e) {
      setStatus('Capture request failed.');
      addLog('Capture error: ' + e.message);
      console.error(e);
    }
  };

  const handleEndSession = async () => {
    try {
      // Send session-closed message to homeowner via WebSocket before closing
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsSend({ type: 'session-closed', roomId });
        // Give the message a moment to send
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Close the session in the database
      if (roomId) {
        try {
          await closeSession(roomId);
          addLog('Session closed in database.');
        } catch (err) {
          console.error('Error closing session:', err);
          addLog('Error closing session: ' + err.message);
        }
      }
    } catch (err) {
      console.error('Error during session end:', err);
    } finally {
      // Clean up connections
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (pcRef.current) {
        pcRef.current.close();
      }
      addLog('Session ended.');
      navigate('/dashboard');
    }
  };

  const handleBackToHome = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (pcRef.current) {
      pcRef.current.close();
    }
    navigate('/dashboard');
  };

  const handleDownloadAll = async () => {
    if (photos.length === 0) {
      alert('No photos to download');
      return;
    }

    try {
      setStatus('Preparing download...');
      addLog('Starting download of ' + photos.length + ' photos...');
      
      const zip = new JSZip();
      
      // Add each photo to the zip
      for (let i = 0; i < photos.length; i++) {
        const url = photos[i];
        const blobUrl = photoBlobUrls[url];
        
        if (blobUrl) {
          try {
            // Fetch the blob from the blob URL
            const response = await fetch(blobUrl);
            const blob = await response.blob();
            
            // Extract filename from the original URL or create one
            const filename = url.split('/').pop() || `photo-${i + 1}.jpg`;
            zip.file(filename, blob);
            
            addLog(`Added ${filename} to zip`);
          } catch (error) {
            addLog(`Failed to add photo ${i + 1}: ${error.message}`);
          }
        }
      }
      
      // Generate the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Create a download link and trigger it
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `session-${roomId}-photos.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the temporary blob URL
      setTimeout(() => URL.revokeObjectURL(link.href), 100);
      
      setStatus('Download complete.');
      addLog('Download complete: session-' + roomId + '-photos.zip');
    } catch (error) {
      setStatus('Download failed.');
      addLog('Download error: ' + error.message);
      console.error(error);
    }
  };

  const handleDeletePhoto = async (url) => {
    if (!roomId) {
      alert('Missing room id.');
      return;
    }

    // Extract filename from URL (e.g., "/captures/Q1YOuD/2025-12-04T21-21-29-682475410Z.jpg")
    const fileName = url.split('/').pop();
    if (!fileName) {
      alert('Could not determine filename');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete this image?`)) {
      return;
    }

    try {
      setStatus('Deleting image...');
      addLog(`Deleting image: ${fileName}`);
      
      const result = await deleteImage(roomId, fileName);
      
      if (result.error) {
        setStatus('Delete failed.');
        addLog('Delete error: ' + result.error);
        alert('Failed to delete image: ' + result.error);
        return;
      }

      // Remove from photos array
      setPhotos(prev => prev.filter(photoUrl => photoUrl !== url));
      
      // Clean up blob URL
      const blobUrl = photoBlobUrls[url];
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setPhotoBlobUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[url];
          return newUrls;
        });
      }
      
      setStatus('Image deleted.');
      addLog(`Image deleted: ${fileName}`);
    } catch (error) {
      setStatus('Delete failed.');
      addLog('Delete error: ' + error.message);
      alert('Failed to delete image: ' + error.message);
      console.error(error);
    }
  };

  // Zoom and pan handlers
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.5, 5));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.5, 1));
    // Reset pan when zooming all the way out
    if (zoom <= 1.5) {
      setPan({ x: 0, y: 0 });
    }
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsPanning(true);
      setPanStart({ 
        x: e.clientX - pan.x, 
        y: e.clientY - pan.y 
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning && zoom > 1) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleTouchStart = (e) => {
    if (zoom > 1 && e.touches.length === 1) {
      setIsPanning(true);
      setPanStart({ 
        x: e.touches[0].clientX - pan.x, 
        y: e.touches[0].clientY - pan.y 
      });
    }
  };

  const handleTouchMove = (e) => {
    if (isPanning && zoom > 1 && e.touches.length === 1) {
      setPan({
        x: e.touches[0].clientX - panStart.x,
        y: e.touches[0].clientY - panStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsPanning(false);
  };

  useEffect(() => {
    // Add global mouse up handler for panning
    const handleGlobalMouseUp = () => {
      setIsPanning(false);
    };
    
    document.addEventListener('mouseup', handleGlobalMouseUp);
    
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
      // Clean up all blob URLs to prevent memory leaks (only on unmount)
      blobUrlsToCleanupRef.current.forEach(blobUrl => {
        if (blobUrl) {
          URL.revokeObjectURL(blobUrl);
        }
      });
    };
  }, []); // Empty dependency array - cleanup only runs on unmount

  return (
    <div className="page">
      <header>
        <img className="logo" src="/assets/logo.png" alt="Stewart Valuation Intelligence" />
        <h1>Remote Inspection</h1>
      </header>

      <main className="main">
        <div className="card" style={{width: '100%', maxWidth: '1000px'}}>
          <h1 style={{color: '#000', marginTop: 0}}>Inspection Console</h1>
          <p className="hint" style={{marginTop: '-6px'}}>
            {roomId ? `Room: ${roomId}` : 'No room id in URL'}
          </p>

          {/* Connection Status */}
          <div style={{
            marginBottom: '16px',
            padding: '12px',
            background: connectionStatus === 'active' ? '#dcfce7' : '#fee2e2',
            borderRadius: '8px',
            border: `1px solid ${connectionStatus === 'active' ? '#86efac' : '#fca5a5'}`,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: connectionStatus === 'active' ? '#22c55e' : '#ef4444',
              display: 'inline-block'
            }}></span>
            <strong style={{
              color: connectionStatus === 'active' ? '#166534' : '#991b1b'
            }}>
              {connectionStatus === 'active' ? 'Active' : 'Inactive'}
            </strong>
          </div>

          <div className="row" style={{margin: '8px 0 16px'}}>
            <button 
              className={`btn ${joined ? 'on' : ''}`}
              onClick={handleJoin}
              disabled={joined}
            >
              {joined ? 'Joined' : 'Join Session'}
            </button>
            <button 
              className="btn btn-secondary"
              onClick={handleToggleAudio}
              disabled={!joined}
            >
              {audioMuted ? '🔇 Unmute Audio' : '🔊 Mute Audio'}
            </button>
            <button 
              className="btn"
              onClick={handleEndSession}
              disabled={!joined}
              style={{background: '#dc2626', borderColor: '#dc2626'}}
            >
              End Session
            </button>
            <button 
              className="btn btn-outline"
              onClick={handleBackToHome}
            >
              Back to Home
            </button>
          </div>
          
          {joinError && (
            <p style={{color: '#dc2626', margin: '-8px 0 12px', fontSize: '14px'}}>
              {joinError}
            </p>
          )}

          {/* Homeowner Location Display */}
          {homeownerLocation && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              background: '#f0f9ff',
              border: '1px solid #3b82f6',
              borderRadius: '8px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <strong style={{ color: '#1e40af', fontSize: '14px' }}>📍 Homeowner Location</strong>
                  <div style={{ marginTop: '4px', fontSize: '13px', color: '#374151' }}>
                    <a
                      href={`https://www.google.com/maps?q=${homeownerLocation.latitude},${homeownerLocation.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#2563eb', 
                        textDecoration: 'none',
                        fontFamily: 'monospace'
                      }}
                    >
                      {homeownerLocation.latitude.toFixed(6)}, {homeownerLocation.longitude.toFixed(6)}
                    </a>
                    {homeownerLocation.accuracy && (
                      <span style={{ marginLeft: '8px', color: '#6b7280' }}>
                        (±{Math.round(homeownerLocation.accuracy)}m)
                      </span>
                    )}
                  </div>
                </div>
                <a
                  href={`https://www.google.com/maps?q=${homeownerLocation.latitude},${homeownerLocation.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline"
                  style={{ padding: '6px 12px', fontSize: '13px', marginLeft: '12px' }}
                >
                  View on Map
                </a>
              </div>
            </div>
          )}

          {/* Remote video tile with Capture Photo inside */}
          <div className="video-tile">
            <h3 style={{margin: '0 0 8px'}}>Homeowner Video</h3>
            
            {/* Video container with zoom/pan */}
            <div 
              ref={videoContainerRef}
              style={{
                position: 'relative',
                width: '100%',
                maxHeight: '500px',
                overflow: 'hidden',
                borderRadius: '12px',
                background: '#000',
                cursor: zoom > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <video 
                ref={remoteVideoRef}
                autoPlay 
                playsInline 
                muted={audioMuted}
                style={{
                  width: '100%', 
                  maxHeight: '500px',
                  display: 'block',
                  objectFit: 'contain',
                  transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                  transformOrigin: 'center center',
                  transition: isPanning ? 'none' : 'transform 0.2s ease-out',
                  pointerEvents: 'none'
                }}
              />
              
              {/* Zoom controls overlay */}
              <div style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                background: 'rgba(0, 0, 0, 0.7)',
                borderRadius: '8px',
                padding: '8px',
                backdropFilter: 'blur(4px)'
              }}>
                <button
                  onClick={handleZoomIn}
                  disabled={zoom >= 5}
                  style={{
                    background: zoom >= 5 ? '#4b5563' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    width: '40px',
                    height: '40px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    cursor: zoom >= 5 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s'
                  }}
                  title="Zoom in"
                >
                  +
                </button>
                <div style={{
                  color: 'white',
                  fontSize: '12px',
                  textAlign: 'center',
                  fontWeight: '600',
                  userSelect: 'none'
                }}>
                  {zoom}×
                </div>
                <button
                  onClick={handleZoomOut}
                  disabled={zoom <= 1}
                  style={{
                    background: zoom <= 1 ? '#4b5563' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    width: '40px',
                    height: '40px',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    cursor: zoom <= 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s'
                  }}
                  title="Zoom out"
                >
                  −
                </button>
                {zoom > 1 && (
                  <button
                    onClick={handleResetZoom}
                    style={{
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      width: '40px',
                      height: '40px',
                      fontSize: '18px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'background 0.2s'
                    }}
                    title="Reset zoom"
                  >
                    ⟲
                  </button>
                )}
              </div>
              
              {/* Pan instruction when zoomed */}
              {zoom > 1 && (
                <div style={{
                  position: 'absolute',
                  bottom: '12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backdropFilter: 'blur(4px)',
                  pointerEvents: 'none',
                  userSelect: 'none'
                }}>
                  Click and drag to pan
                </div>
              )}
            </div>

            <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px'}}>
              <button 
                className="btn btn-outline"
                onClick={handleCapturePhoto}
                disabled={!joined}
              >
                Capture Photo
              </button>
              <span className="hint">
                Requests homeowner to capture a photo. Use zoom to inspect details.
              </span>
            </div>
          </div>

          <div className="section">
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px'}}>
              <h3 style={{margin: 0}}>Captured Photos</h3>
              {photos.length > 0 && (
                <button 
                  className="btn btn-outline"
                  onClick={handleDownloadAll}
                  style={{padding: '6px 16px', fontSize: '14px'}}
                >
                  Download All ({photos.length})
                </button>
              )}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: '12px'
            }}>
              {photos.map((url, index) => {
                const blobUrl = photoBlobUrls[url];
                return (
                  <div key={index} style={{ position: 'relative' }}>
                    {blobUrl ? (
                      <>
                        <a href={blobUrl} target="_blank" rel="noopener noreferrer">
                          <img 
                            src={blobUrl} 
                            alt="Captured photo"
                            style={{
                              width: '100%',
                              display: 'block',
                              borderRadius: '8px',
                              border: '1px solid #e5e7eb'
                            }}
                          />
                        </a>
                        <button
                          onClick={() => handleDeletePhoto(url)}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                          }}
                          title="Delete image"
                        >
                          ×
                        </button>
                      </>
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '140px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb',
                        background: '#f3f4f6',
                        color: '#6b7280'
                      }}>
                        Loading...
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}

export default AppraiserConsole;
